import { FunctionComponent, useRef, useState } from "react";

import {
    Box,
    IconButton,
    Button,
    Flex,
    Heading,
    Tooltip,
    LinkBox,
    LinkOverlay,
    Alert,
    AlertIcon,
    AlertDescription,
    AlertTitle,
    Image,
    Text,
    Icon,
} from "@chakra-ui/react";
import { IconClock, IconPhotoOff, IconPlus, IconUser } from "@tabler/icons-react";
import { IconEdit, IconEye, IconEyeOff } from "@tabler/icons-react";
import { PageLoading } from "am-tax-fe-core";
import { format } from "date-fns";
import { Link } from "react-router-dom";

import { Report, useEngagementReports, useEngagementToggleReportDashboard, useProjectReports, useProjectToggleReportDashboard } from "../../api/index.ts";
import { AreaPermissions, ReportArea, SecurityArea } from "../../auth/index.ts";
import { Page } from "../Page.tsx";

import EditReportDialog from "./EditReportDialog.tsx";

export type Props = {
    engagementId: string;
    projectId?: string;
    areaPermissions: AreaPermissions<SecurityArea>;
    enableToggleDashboard?: boolean;
    isInternaluser: boolean;
};

export const BaseReportsPage: FunctionComponent<Props> = ({ engagementId, projectId, areaPermissions, isInternaluser }) => {
    const focusAfterClosingRef = useRef(null);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [selectedReport, setSelectedReport] = useState<Report | undefined>();

    const { mutate: toggleProjectPublish, isPending: toggleProjectPublishPending } = useProjectToggleReportDashboard();
    const { mutate: toggleEngagementPublish, isPending: toggleEngagementPublishPending } = useEngagementToggleReportDashboard();
    const { data: engagementData, isLoading: isEngLoadingReports } = useEngagementReports(engagementId, !projectId);
    const { data: projectData, isLoading: isProjLoadingReports } = useProjectReports(engagementId, projectId!);
    const reportData = projectId ? projectData : engagementData;

    const togglePublishPending = projectId ? toggleProjectPublishPending : toggleEngagementPublishPending;
    const isLoadingReports = projectId ? isProjLoadingReports : isEngLoadingReports;

    const handleTogglePublish = (report: Report) => {
        const args = { engagementId, ...report, isPublishedToClient: !report.isPublishedToClient };
        setSelectedReport(report);
        if (!projectId) {
            toggleEngagementPublish(args);
        } else toggleProjectPublish({ ...args, projectId });
    };

    const handleEditReport = (report?: Report) => {
        setSelectedReport(report);
        setShowEditDialog(true);
    };

    const getToolbar = (report: Report) => (
        <Flex pl="1rem" gap=".5rem">
            {areaPermissions.has(ReportArea.update) && (
                <>
                    <Tooltip label={report.isPublishedToClient ? "Hide report from clients" : "Hidden from clients"}>
                        <IconButton
                            aria-label="Hide comment from clients"
                            variant="naked"
                            icon={report.isPublishedToClient ? <IconEye size={24} /> : <IconEyeOff size={24} />}
                            onClick={() => handleTogglePublish(report)}
                            isLoading={report.id === selectedReport?.id && togglePublishPending}
                        />
                    </Tooltip>
                    <Tooltip label="Edit report">
                        <IconButton aria-label="Edit report" variant="naked" icon={<IconEdit size={24} />} onClick={() => handleEditReport(report)} />
                    </Tooltip>
                </>
            )}
        </Flex>
    );

    const tiles = reportData?.map(report => {
        return (
            <LinkBox
                bg={report.isPublishedToClient ? "white" : "blackAlpha.50"}
                as="article"
                key={report.id}
                width={["100%", null, "24rem"]}
                height={["fit-content", null, "32rem"]}
                minHeight="12rem"
                minWidth="12rem"
                borderWidth="1px"
                borderTop="4px solid var(--chakra-colors-blue-600)"
                boxShadow="3px 3px 3px 0px rgba(0,0,0,0.1)"
                rounded="md"
                p="1rem"
            >
                <Flex justifyContent="center" alignItems="flex-start" mb="1rem">
                    <Heading fontSize="lg" fontWeight="500" width="100%" mb={0}>
                        <LinkOverlay as={Link} to={`report/${report.id}`}>
                            {report.title}
                        </LinkOverlay>
                    </Heading>
                    {getToolbar(report)}
                </Flex>

                <Box
                    display="flex"
                    justifyContent="center"
                    borderRadius="lg"
                    border={report.imageFile ? "solid 1px var(--chakra-colors-gray-400)" : "solid 2px var(--chakra-colors-blue-200)"}
                    background="blue.25"
                    height={["40%", "35%", "35%"]}
                >
                    {report.imageFileName ? (
                        <Image
                            src={"data:image/png;base64," + report.imageFile}
                            alt={report.imageFileName}
                            borderRadius="sm"
                            height="100%"
                            width="100%"
                            objectFit="cover"
                        />
                    ) : (
                        <Icon as={IconPhotoOff} color="blue.200" objectFit="cover" strokeWidth="1" height="100%" width="100%" />
                    )}
                </Box>
                <Box display="flex" flexWrap="wrap" p="0.5rem" fontSize={["0.9rem", "0.8rem"]}>
                    <strong>{report.reportName}</strong>
                </Box>

                <Box py="1rem" my="0.5rem" borderY={"solid 2px var(--chakra-colors-gray-200)"} fontSize={["0.75em"]} height={"40%"}>
                    {report.description ? (
                        <Box ml="0.5rem" pl="0.5rem" borderLeft="solid 3px var(--chakra-colors-blue-300)">
                            <>{report.description}</>
                        </Box>
                    ) : (
                        <Box display="flex" justifyContent="center">
                            <Text as="i">No description available</Text>
                        </Box>
                    )}
                </Box>

                <Flex wrap="wrap" fontSize="0.9rem">
                    <Box display="flex" alignItems="center" gap="0.5rem" mr="1rem" overflowWrap="anywhere">
                        <Icon as={IconUser} color="blue.800" aria-label="created by" />
                        {report.createdBy}
                    </Box>
                    {report.updatedOn && (
                        <Box display="flex" alignItems="center" gap="0.5rem" overflowWrap="anywhere">
                            <Icon as={IconClock} color="blue.800" aria-label="created on" />
                            {format(report.updatedOn, "MM/dd/yyyy")}
                        </Box>
                    )}
                </Flex>
            </LinkBox>
        );
    });

    const tileContainer = (
        <Flex gap="1rem" wrap="wrap">
            {tiles && tiles.length > 0 ? (
                tiles
            ) : (
                <>
                    {!isInternaluser ? (
                        <Alert status="info" variant="subtle" flexDirection="column">
                            <Flex ml="1rem" alignItems="center" width="100%">
                                <AlertIcon boxSize="40px" mr="1rem" />
                                <Box>
                                    <AlertTitle mt={4} mb={1} fontSize="lg">
                                        Start using Reports
                                    </AlertTitle>
                                    <AlertDescription textAlign="left">
                                        There are no active Reports enabled for this engagement. Please contact Engagement/Project Administrator to enable
                                        reports.
                                    </AlertDescription>
                                </Box>
                            </Flex>
                        </Alert>
                    ) : (
                        <Alert status="info" variant="subtle" flexDirection="column">
                            <Flex ml="1rem" alignItems="center" width="100%">
                                <AlertIcon boxSize="40px" mr="1rem" />
                                <Box>
                                    <AlertTitle mt={4} mb={1} fontSize="lg">
                                        Start using Reports
                                    </AlertTitle>
                                    <AlertDescription textAlign="left">
                                        There are no reports yet. Add a new report by pressing the button at the top of the screen.
                                    </AlertDescription>
                                </Box>
                            </Flex>
                        </Alert>
                    )}
                </>
            )}
        </Flex>
    );

    if (isLoadingReports) {
        return <PageLoading text={"Loading Reports..."} />;
    }

    return (
        <Page title="Reports">
            {!!isInternaluser && (
                <Flex mb={"1rem"} gap={"2rem"} justifyContent="flex-end">
                    {areaPermissions.has(ReportArea.create) && (
                        <Button leftIcon={<IconPlus />} onClick={() => handleEditReport()} px={"1rem"} ref={focusAfterClosingRef}>
                            Add Report
                        </Button>
                    )}
                </Flex>
            )}
            {tileContainer}
            <EditReportDialog
                engagementId={engagementId}
                projectId={projectId}
                report={selectedReport}
                show={showEditDialog}
                closeDialog={() => setShowEditDialog(false)}
                focusAfterClosingRef={focusAfterClosingRef}
            />
        </Page>
    );
};
